import { Buffer } from "buffer";
import _ from "lodash";
import { registerABI, registerFormatter, registerContract } from "@/contractRegistry";
import { registerAPI } from "@/apiRegistry";

import { api, apy, currency, cashFlowCharts } from "@/config";
import { BNToDecimal, compareWithMaxUint, convertToDays, toDecimal } from "@/helpers/number_format_helper";
import {
  convertCashflow,
  convertResponse,
  setValueAsPercentageWithMax,
  makeQueryParams,
  addDaysParams,
  addOverrides,
} from "@/helpers/api_calls";

import IERC20Metadata from "@openzeppelin/contracts/build/contracts/IERC20Metadata.json";
import EToken from "@ensuro/core/build/contracts/EToken.sol/EToken.json";
import RiskModule from "@ensuro/core/build/contracts/RiskModule.sol/RiskModule.json";
import PremiumsAccount from "@ensuro/core/build/contracts/PremiumsAccount.sol/PremiumsAccount.json";
import PolicyPool from "@ensuro/core/build/contracts/PolicyPool.sol/PolicyPool.json";
import Cooler from "@ensuro/core/build/contracts/Cooler.sol/Cooler.json";
import ERC20Permit from "@openzeppelin/contracts/build/contracts/ERC20Permit.json";
import IERC4626 from "@openzeppelin/contracts/build/contracts/IERC4626.json";
import ERC20Faucet from "./abis/ERC20Faucet.json";
import ILPWhitelist from "@ensuro/core/build/contracts/interfaces/ILPWhitelist.sol/ILPWhitelist.json";

registerABI("ERC20", IERC20Metadata.abi);
registerABI("EToken", EToken.abi);
registerABI("RiskModule", RiskModule.abi);
registerABI("PremiumsAccount", PremiumsAccount.abi);
registerABI("PolicyPool", PolicyPool.abi);
registerABI("Cooler", Cooler.abi);
registerABI("ERC20Permit", ERC20Permit.abi);
registerABI("FaucetUSDC", ERC20Faucet.abi);
registerABI("ILPWhitelist", ILPWhitelist.abi);
registerABI("WEToken", IERC4626.abi);

registerFormatter("ERC20", "balanceOf", _.partial(BNToDecimal, _, currency.decimals));
registerFormatter("ERC20", "totalSupply", _.partial(BNToDecimal, _, currency.decimals));
registerFormatter("ERC20", "allowance", _.partial(BNToDecimal, _, currency.decimals));

registerFormatter("ERC20Permit", "nonces", (v: unknown) => (v != null ? Number(v) : undefined));
// EIP712_REVISION returns bytes (e.g. "1" as 0x31); decode to string for EIP-712 domain
registerFormatter("ERC20Permit", "EIP712_REVISION", (v: unknown) => {
  if (v == null || v === undefined) return undefined;
  try {
    const hex = typeof v === "string" ? v : (v as { hex?: string })?.hex;
    if (!hex || typeof hex !== "string") return undefined;
    return Buffer.from(hex.startsWith("0x") ? hex.slice(2) : hex, "hex").toString("utf8") || undefined;
  } catch {
    return undefined;
  }
});

registerContract(currency.address, "ERC20Permit");

registerFormatter("EToken", "balanceOf", _.partial(BNToDecimal, _, currency.decimals));
registerFormatter("EToken", "totalWithdrawable", _.partial(BNToDecimal, _, currency.decimals));
registerFormatter("EToken", "totalSupply", _.partial(BNToDecimal, _, currency.decimals));
registerFormatter("EToken", "allowance", _.partial(BNToDecimal, _, currency.decimals));
registerFormatter("EToken", "scr", _.partial(BNToDecimal, _, currency.decimals));
registerFormatter("EToken", "internalLoanInterestRate", _.partial(BNToDecimal, _, 18));
registerFormatter("EToken", "maxUtilizationRate", _.partial(BNToDecimal, _, 18));
registerFormatter("EToken", "minUtilizationRate", _.partial(BNToDecimal, _, 18));
registerFormatter("EToken", "liquidityRequirement", _.partial(BNToDecimal, _, 18));
registerFormatter("EToken", "scrInterestRate", _.partial(BNToDecimal, _, 18));
registerFormatter("EToken", "tokenInterestRate", _.partial(BNToDecimal, _, 18));
registerFormatter("EToken", "yieldVault", undefined);
registerFormatter("EToken", "getLoan", _.partial(BNToDecimal, _, currency.decimals));

registerFormatter("PremiumsAccount", "deficitRatio", _.partial(BNToDecimal, _, 18));
registerFormatter("PremiumsAccount", "borrowedActivePP", _.partial(BNToDecimal, _, currency.decimals));
registerFormatter("PremiumsAccount", "activePurePremiums", _.partial(BNToDecimal, _, currency.decimals));
registerFormatter("PremiumsAccount", "wonPurePremiums", _.partial(BNToDecimal, _, currency.decimals));
registerFormatter("PremiumsAccount", "purePremiums", _.partial(BNToDecimal, _, currency.decimals));
registerFormatter("PremiumsAccount", "surplus", _.partial(BNToDecimal, _, currency.decimals));
registerFormatter("PremiumsAccount", "jrLoanLimit", _.partial(compareWithMaxUint, _, currency.decimals));
registerFormatter("PremiumsAccount", "srLoanLimit", _.partial(compareWithMaxUint, _, currency.decimals));
registerFormatter("PremiumsAccount", "juniorEtk", undefined);
registerFormatter("PremiumsAccount", "seniorEtk", undefined);
registerFormatter("PremiumsAccount", "yieldVault", undefined);

registerFormatter("ILPWhitelist", "acceptsDeposit(address,address,uint256)", undefined);
registerFormatter("ILPWhitelist", "acceptsWithdrawal(address,address,uint256)", undefined);

// WEToken (ERC-4626 wrapper): convertToShares(assets) returns shares in wads (18 decimals)
registerFormatter("WEToken", "convertToShares", _.partial(BNToDecimal, _, 18));
registerFormatter("WEToken", "convertToAssets", _.partial(BNToDecimal, _, currency.decimals));
registerFormatter("WEToken", "balanceOf", _.partial(BNToDecimal, _, 18));
registerFormatter("WEToken", "maxWithdraw", _.partial(BNToDecimal, _, currency.decimals));

// PolicyPool.getExposure(IRiskModule) returns (active, limit) as tuple;
registerFormatter("PolicyPool", "getExposure", (value: unknown) => {
  const [active, limit] = (value as [unknown, unknown]) ?? [];
  const toNum = (v: unknown) => (v != null ? BNToDecimal(BigInt(Number(v)), currency.decimals) : undefined);
  return { active: toNum(active), limit: toNum(limit) };
});

/** EToken Endpoints **/
registerAPI(
  "apy",
  (address: string, days: number = apy.range) => `${api.url}/etokens/${address}/apr/?days_from=${days}`,
  (response: any) => Math.min(toDecimal(response.apy).toNumber(), toDecimal(apy.maxAPY).toNumber())
);
registerAPI(
  "apyHistory",
  (address: string, daysFrom: number = cashFlowCharts.daysFrom, daysTo?: number, days: number = apy.range) =>
    `${api.url}/etokens/${address}/apr_history/${makeQueryParams(addDaysParams(daysFrom, daysTo, days))}`,
  (response: any) => setValueAsPercentageWithMax(response.data, "apy", 200)
);
registerAPI(
  "scrBreakdown",
  (address: string) => `${api.url}/etokens/${address}/scr_breakdown/`,
  (response: any) => convertResponse(response.data, "scr")
);
registerAPI(
  "paScrBreakdown",
  (address: string) => `${api.url}/etokens/${address}/pa_scr_breakdown/`,
  (response: any) => convertResponse(response.data, "scr")
);
registerAPI(
  "lockup",
  (address: string) => `${api.url}/etokens/${address}/lockup/`,
  (response: any) => convertResponse(response.data, "scr")
);
registerAPI(
  "etkScrHistory",
  (address: string, daysFrom: number = cashFlowCharts.daysFrom, daysTo?: number) =>
    `${api.url}/etokens/${address}/scr_history/${makeQueryParams(addDaysParams(daysFrom, daysTo))}`,
  (response: any) => response.data
);
registerAPI(
  "totalSupplyHistory",
  (address: string, daysFrom: number = cashFlowCharts.daysFrom, daysTo?: number) =>
    `${api.url}/etokens/${address}/liquidity_history/${makeQueryParams(addDaysParams(daysFrom, daysTo))}`,
  (response: any) => response.data
);
registerAPI(
  "urHistory",
  (address: string, daysFrom: number = cashFlowCharts.daysFrom, daysTo?: number) =>
    `${api.url}/etokens/${address}/ur_history/${makeQueryParams(addDaysParams(daysFrom, daysTo))}`,
  (response: any) => setValueAsPercentageWithMax(response.data, "ur")
);
registerAPI(
  "etkOwners",
  (address: string, top: number = 10) => `${api.url}/etokens/${address}/lps_breakdown/?top=${top}`,
  (response: any) => response.data
);
registerAPI(
  "compositionHistory",
  (address: string, period: number = 1) => `${api.url}/etokens/${address}/composition_history/?period=${period}`,
  (response: any) => response.data
);

/** Risk Module Endpoints **/
registerAPI(
  "activePolicies",
  (address: string) => `${api.url}/policies/?rm=${address}&status=active&limit=1`,
  (response: any) => response.count
);
registerAPI(
  "surplus",
  (address: string) =>
    `${api.url}/riskmodules/${address}/surplus/${makeQueryParams(
      { wonpremiums: true, earnings: true },
      addOverrides(address)
    )}`,
  (response: any) => toDecimal(response.surplus)
);
registerAPI(
  "maturedSurplus",
  (address: string) =>
    `${api.url}/riskmodules/${address}/matured_surplus/${makeQueryParams(
      { wonpremiums: true, earnings: true },
      addOverrides(address)
    )}`,
  (response: any) => toDecimal(response.matured_surplus)
);
registerAPI(
  "gwp",
  (address: string) => `${api.url}/riskmodules/${address}/gwp/`,
  (response: any) => toDecimal(response.gwp)
);
registerAPI(
  "scr",
  (address: string) => `${api.url}/riskmodules/${address}/scr/`,
  (response: any) => toDecimal(response.scr)
);
registerAPI(
  "cashflow",
  (address: string, daysFrom: number = cashFlowCharts.daysFrom, daysTo?: number) =>
    `${api.url}/riskmodules/${address}/cashflow/${makeQueryParams(addDaysParams(daysFrom, daysTo))}`,
  (response: any) => convertCashflow(response.data)
);
registerAPI(
  "surplusHistory",
  (address: string, daysFrom: number = cashFlowCharts.daysFrom, daysTo?: number) =>
    `${api.url}/riskmodules/${address}/surplus_history/${makeQueryParams(
      addDaysParams(daysFrom, daysTo),
      { wonpremiums: true, earnings: true },
      addOverrides(address)
    )}`,
  (response: any) => response.data
);
registerAPI(
  "maturedSurplusHistory",
  (address: string, daysFrom: number = cashFlowCharts.daysFrom, daysTo?: number) =>
    `${api.url}/riskmodules/${address}/matured_surplus_history/${makeQueryParams(
      addDaysParams(daysFrom, daysTo),
      { wonpremiums: true, earnings: true },
      addOverrides(address)
    )}`,
  (response: any) => response.data
);
registerAPI(
  "activePoliciesHistory",
  (address: string, daysFrom: number = cashFlowCharts.daysFrom, daysTo?: number) =>
    `${api.url}/riskmodules/${address}/active_policies/${makeQueryParams(addDaysParams(daysFrom, daysTo))}`,
  (response: any) => response.data
);
registerAPI(
  "gwpHistory",
  (address: string, daysFrom: number = cashFlowCharts.daysFrom, daysTo?: number) =>
    `${api.url}/riskmodules/${address}/gwp_history/${makeQueryParams(addDaysParams(daysFrom, daysTo))}`,
  (response: any) => response.data
);
registerAPI(
  "scrHistory",
  (address: string, daysFrom: number = cashFlowCharts.daysFrom, daysTo?: number) =>
    `${api.url}/riskmodules/${address}/scr_history/${makeQueryParams(addDaysParams(daysFrom, daysTo))}`,
  (response: any) => response.data
);
registerAPI(
  "activePremiums",
  (address: string, daysFrom: number = cashFlowCharts.daysFrom, daysTo?: number) =>
    `${api.url}/riskmodules/${address}/active_premiums/${makeQueryParams(addDaysParams(daysFrom, daysTo))}`,
  (response: any) => response.data
);
registerAPI(
  "rmByPremiumsAccount",
  (paAddress: string) => `${api.url}/riskmodules/?premiums_account=${paAddress}`,
  (response: any) => response
);
registerAPI(
  "wonPremiumsInOut",
  (address: string, daysFrom: number = cashFlowCharts.daysFrom, daysTo?: number) =>
    `${api.url}/riskmodules/${address}/wonpremiumsinout/${makeQueryParams(addDaysParams(daysFrom, daysTo))}`,
  (response: any) => response.data
);

registerAPI(
  "avgPayoutPerPolicy",
  (address: string) => `${api.url}/riskmodules/${address}/avg_payout_per_policy/`,
  (response: any) => toDecimal(response.avg_payout_per_policy)
);

registerAPI(
  "avgPolicyDuration",
  (address: string) => `${api.url}/riskmodules/${address}/avg_policy_duration/`,
  (response: any) => convertToDays(response.avg_policy_duration)
);

registerAPI(
  "rmParams",
  (address: string) => `${api.url}/riskmodules/${address}/params/`,
  (response: any) => response.params
);

/* LPs Endpoints  */
registerAPI(
  "lpsDeposits",
  (address: string) => `${api.url}/lps/${address}/`,
  (response: any) => response.net_deposits
);

registerAPI(
  "lpEvents",
  (etkAddress: string, order?: string, limit: number = 10) =>
    `${api.url}/lpevents/?e_token=${etkAddress}` + (order ? "&o=" + order : "") + "&limit=" + limit,
  (response: any) => response.results
);

/* Premiums Account Endpoints */
registerAPI(
  "paGwp",
  (address: string) => `${api.url}/premiumsaccounts/${address}/gwp/`,
  (response: any) => toDecimal(response.gwp)
);
registerAPI(
  "paGwpHistory",
  (address: string, daysFrom: number = cashFlowCharts.daysFrom, daysTo?: number) =>
    `${api.url}/premiumsaccounts/${address}/gwp_history/${makeQueryParams(addDaysParams(daysFrom, daysTo))}`,
  (response: any) => response.data
);
registerAPI(
  "paScr",
  (address: string) => `${api.url}/premiumsaccounts/${address}/scr/`,
  (response: any) => toDecimal(response.scr)
);
registerAPI(
  "paScrHistory",
  (address: string, daysFrom: number = cashFlowCharts.daysFrom, daysTo?: number) =>
    `${api.url}/premiumsaccounts/${address}/scr_history/${makeQueryParams(addDaysParams(daysFrom, daysTo))}`,
  (response: any) => response.data
);
registerAPI(
  "paActivePolicies",
  (address: string) => `${api.url}/premiumsaccounts/${address}/active_policies/`,
  (response: any) => toDecimal(response.active_policies)
);
registerAPI(
  "paActivePoliciesHistory",
  (address: string, daysFrom: number = cashFlowCharts.daysFrom, daysTo?: number) =>
    `${api.url}/premiumsaccounts/${address}/active_policies_history/${makeQueryParams(
      addDaysParams(daysFrom, daysTo)
    )}`,
  (response: any) => response.data
);
registerAPI(
  "paSurplus",
  (address: string) =>
    `${api.url}/premiumsaccounts/${address}/surplus/${makeQueryParams(
      { wonpremiums: true, earnings: true },
      addOverrides(address)
    )}`,
  (response: any) => toDecimal(response.surplus)
);
registerAPI(
  "paSurplusHistory",
  (address: string, daysFrom: number = cashFlowCharts.daysFrom, daysTo?: number) =>
    `${api.url}/premiumsaccounts/${address}/surplus_history/${makeQueryParams(
      addDaysParams(daysFrom, daysTo),
      { wonpremiums: true, earnings: true },
      addOverrides(address)
    )}`,
  (response: any) => response.data
);
registerAPI(
  "paMaturedSurplus",
  (address: string) =>
    `${api.url}/premiumsaccounts/${address}/matured_surplus/${makeQueryParams(
      { wonpremiums: true, earnings: true },
      addOverrides(address)
    )}`,
  (response: any) => toDecimal(response.matured_surplus)
);
registerAPI(
  "paMaturedSurplusHistory",
  (address: string, daysFrom: number = cashFlowCharts.daysFrom, daysTo?: number) =>
    `${api.url}/premiumsaccounts/${address}/matured_surplus_history/${makeQueryParams(
      addDaysParams(daysFrom, daysTo),
      { wonpremiums: true, earnings: true },
      addOverrides(address)
    )}`,
  (response: any) => response.data
);
registerAPI(
  "paActivePremiums",
  (address: string) => `${api.url}/premiumsaccounts/${address}/active_premiums/`,
  (response: any) => toDecimal(response.active_premiums)
);
registerAPI(
  "paActivePremiumsHistory",
  (address: string, daysFrom: number = cashFlowCharts.daysFrom, daysTo?: number) =>
    `${api.url}/premiumsaccounts/${address}/active_premiums_history/${makeQueryParams(
      addDaysParams(daysFrom, daysTo)
    )}`,
  (response: any) => response.data
);
registerAPI(
  "paCashflow",
  (paAddress: string, daysFrom: number = cashFlowCharts.daysFrom, daysTo?: number) =>
    `${api.url}/premiumsaccounts/${paAddress}/cashflow/${makeQueryParams(addDaysParams(daysFrom, daysTo))}`,
  (response: any) => convertCashflow(response.data)
);
registerAPI(
  "rmBreakdown",
  (paAddress: string) => `${api.url}/premiumsaccounts/${paAddress}/rm_breakdown/`,
  (response: any) => convertResponse(response.data, "active_pure_premiums")
);
registerAPI(
  "paWonPremiumsInOut",
  (address: string, daysFrom: number = cashFlowCharts.daysFrom, daysTo?: number) =>
    `${api.url}/premiumsaccounts/${address}/wonpremiumsinout/${makeQueryParams(addDaysParams(daysFrom, daysTo))}`,
  (response: any) => response.data
);

/* Wallet Endpoints */
registerAPI(
  "checkUser",
  (address: string) => `${api.url}/wallet/${address}/`,
  (response: any) => response
);

registerAPI("storeKYC", () => `${api.url}/wallet/`, undefined, "POST");
